/*
 * release-countdown.js
 *
 * Drives the PDP "Release Countdown" product block
 * (snippets/product-block-release-countdown.liquid).
 *
 * The browser clock is the single source of truth for show/hide: Liquid 'now'
 * is unreliable on CDN-cached PDPs, so the server always emits the markup +
 * target instant (data-release-epoch, absolute Unix seconds) and this script
 * decides whether the drop is still upcoming, runs the per-second countdown,
 * and hides the block once the launch time passes.
 *
 * Conventions mirror assets/multi-column-load-more.js: IIFE, 'use strict',
 * ES5 var, readyState check, shopify:section:load/unload handling, [data-*]
 * lookups.
 */
(function () {
  'use strict';

  var ENDED_CLASS = 'release-countdown--ended';
  var FLIP_CLASS = 'is-flipping';
  var FLIP_MS = 450; // keep in sync with the CSS keyframe duration

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function setUnit(el, unit, value) {
    var node = el.querySelector('[data-unit="' + unit + '"]');
    if (!node) return;
    var next = pad(value);
    if (node.textContent === next) return; // only touch the DOM (and flip) on change
    node.textContent = next;
    node.classList.add(FLIP_CLASS);
    // Use a timer rather than animationend so reduced-motion (no animation) still clears it.
    window.setTimeout(function () {
      node.classList.remove(FLIP_CLASS);
    }, FLIP_MS);
  }

  function announce(el, message) {
    var status = el.querySelector('[data-release-status]');
    if (status && status.textContent !== message) {
      status.textContent = message;
    }
  }

  function stop(el) {
    if (el._rcTimer) {
      window.clearInterval(el._rcTimer);
      el._rcTimer = null;
    }
  }

  function end(el) {
    stop(el);
    setUnit(el, 'days', 0);
    setUnit(el, 'hours', 0);
    setUnit(el, 'minutes', 0);
    setUnit(el, 'seconds', 0);
    // Hide via the purpose-built class only: .release-countdown--ended uses
    // `display:none !important`, which also removes it from the a11y tree.
    // (el.hidden was redundant and weaker — it can be overridden by a class
    // that sets `display`.)
    el.classList.add(ENDED_CLASS);
    announce(el, 'This product has launched.');
  }

  function tick(el, endMs) {
    var remaining = endMs - Date.now();
    if (remaining <= 0) {
      end(el);
      return;
    }
    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    setUnit(el, 'days', days);
    setUnit(el, 'hours', hours);
    setUnit(el, 'minutes', minutes);
    setUnit(el, 'seconds', seconds);
  }

  // Resolve the launch instant (ms). data-release-epoch is an absolute Unix
  // epoch in SECONDS computed server-side (store-local, timezone-independent).
  function parseEnd(el) {
    var epoch = parseInt(el.getAttribute('data-release-epoch'), 10);
    return (!isNaN(epoch) && epoch > 0) ? epoch * 1000 : NaN;
  }

  function init(el) {
    if (!el || el.getAttribute('data-rc-init') === 'true') return;

    var endMs = parseEnd(el);
    if (isNaN(endMs) || endMs <= 0) return; // bad/empty date — leave the block as the server rendered it

    el.setAttribute('data-rc-init', 'true');

    if (endMs - Date.now() <= 0) {
      // Already launched (e.g. served from a stale cache): hide without a flash of a live timer.
      end(el);
      return;
    }

    tick(el, endMs); // paint immediately so we don't wait 1s for the first frame
    el._rcTimer = window.setInterval(function () {
      tick(el, endMs);
    }, 1000);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-release-countdown]');
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when a section is (re)loaded...
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  // ...and clear any running intervals when a section is removed, so repeated
  // edits in the editor don't stack duplicate timers.
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    var nodes = e.target.querySelectorAll('[data-release-countdown]');
    Array.prototype.forEach.call(nodes, stop);
  });
})();
